import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

type AppRole = 'super_admin' | 'admin' | 'manager' | 'user';

interface RoleGuardProps {
  /** Roles allowed to access the wrapped route(s) */
  roles: AppRole[];
  /** Where to redirect if the user's role is not in the allowed list */
  redirectTo?: string;
  children: ReactNode;
}

/**
 * Route-level role guard.
 * Wraps children and redirects to /dashboard (or custom) if the current
 * user's role is not in the `roles` whitelist.
 *
 * Usage in App.tsx:
 *   <Route path="/admin-portal" element={
 *     <RoleGuard roles={['super_admin']}>
 *       <PFull><AdminPortal /></PFull>
 *     </RoleGuard>
 *   } />
 */
export default function RoleGuard({ roles, redirectTo = '/dashboard', children }: RoleGuardProps) {
  const { userRole, loading } = useAuth();

  // While auth is loading, don't redirect — avoid flash
  if (loading) return null;

  // If we don't know the role yet, wait
  if (!userRole) return null;

  if (!roles.includes(userRole as AppRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
